import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SectionHeader } from '../components/shared/SectionHeader';
import { FeatureCard } from '../components/shared/FeatureCard';
import { CTAStrip } from '../components/shared/CTAStrip';
import {
  ArrowRight,
  FileText,
  ClipboardCheck,
  Settings,
  FileOutput,
  Paperclip,
  Droplets,
  QrCode,
  Upload,
  History,
  LayoutDashboard,
  Bell,
  Layers
} from 'lucide-react';

const DASHBOARD_MOCKUP = "https://customer-assets.emergentagent.com/job_4b0cd8b7-dd81-4c3b-b959-0027fd70ebf1/artifacts/5fnnttlf_product_overview.png";

const capabilities = [
  {
    icon: FileText,
    title: "Application Intake & Validation",
    description: "Digital submission of certificate applications with automated validation against export requirements and document completeness checks."
  },
  {
    icon: ClipboardCheck,
    title: "Inspection Workflows",
    description: "Queue-based inspection assignment by office location, with support for individual and pooled inspector allocation."
  },
  {
    icon: Settings,
    title: "Rules Engine",
    description: "Configurable export requirements by commodity and destination country. Ensures applications meet specific criteria before processing."
  },
  {
    icon: FileOutput,
    title: "Document Generation",
    description: "Official certificate PDFs with fixed-layout templates that maintain consistent formatting across all issuances."
  },
  {
    icon: Paperclip,
    title: "Annexures",
    description: "Support for overflow content without breaking the main certificate layout. Attach additional pages for detailed product lists or declarations."
  },
  {
    icon: Droplets,
    title: "Watermarks & Security Text",
    description: "Diagonal security text and official watermarks embedded in all generated certificates for authenticity."
  },
  {
    icon: QrCode,
    title: "Barcode & QR Verification",
    description: "Each certificate includes unique barcodes and QR codes that trade partners can scan to verify authenticity."
  },
  {
    icon: Upload,
    title: "Document Uploads",
    description: "Secure upload and preview for PDF, JPG, PNG, and Excel files. Attached evidence and supporting documents stored centrally."
  },
  {
    icon: History,
    title: "Audit Trails",
    description: "Complete activity history tracking every action on applications, inspections, and certificates for compliance and accountability."
  },
  {
    icon: LayoutDashboard,
    title: "Role-based Dashboards",
    description: "Tailored interfaces for exporters, inspectors, supervisors, and administrators with relevant metrics and actions."
  },
  {
    icon: Bell,
    title: "Notifications Framework",
    description: "Event-driven notifications via email and in-app alerts. Keep stakeholders informed at every stage of the process."
  },
  {
    icon: Layers,
    title: "Clean Architecture",
    description: "Built with modular, maintainable design principles. Scalable infrastructure ready for national deployment."
  }
];

export default function ProductPage() {
  return (
    <div data-testid="product-page">
      {/* Hero */}
      <section className="section-padding bg-slate-50 leaf-pattern">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="badge-green mb-4 inline-block">Product Overview</span>
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                The complete platform for 
                <span className="text-[#1F7A3A]"> phytosanitary certification</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Certisfy provides a comprehensive, cloud-based solution for managing the entire 
                phytosanitary certification lifecycle—from application intake to official certificate 
                issuance. Designed for national plant protection organizations, inspection teams, and exporters.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                <strong>Phytosanitary certificates</strong> are official documents that certify plants and 
                plant products meet importing countries' health requirements. They're essential for 
                international agricultural trade, confirming shipments are free from quarantine pests and diseases.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?demo=true" data-testid="product-demo-cta">
                  <Button className="bg-[#1F7A3A] text-white hover:bg-[#145A2A] rounded-full px-8 py-3 font-semibold shadow-lg hover:shadow-xl inline-flex items-center gap-2">
                    Request a Demo
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/modules" data-testid="product-modules-cta">
                  <Button variant="outline" className="border-2 border-[#1F7A3A] text-[#1F7A3A] hover:bg-[#F0FDF4] rounded-full px-8 py-3 font-semibold">
                    Explore Modules
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-float">
                <img 
                  src={DASHBOARD_MOCKUP} 
                  alt="Certisfy Dashboard Overview"
                  className="w-full h-auto"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#1F7A3A]/10 rounded-2xl -z-10" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#C8A24A]/10 rounded-full -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section data-testid="capabilities" className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader 
            badge="Capabilities"
            title="Everything you need for export certification"
            subtitle="A complete toolkit covering every aspect of the phytosanitary certification process"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {capabilities.map((capability, index) => (
              <FeatureCard
                key={index}
                icon={capability.icon}
                title={capability.title}
                description={capability.description}
                variant="minimal"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section data-testid="architecture" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge-gold mb-4 inline-block">Technical Foundation</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Built for scale and reliability
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Certisfy is designed with clean architecture principles—modular, maintainable, 
              and ready for national-scale deployment. Our cloud-native infrastructure ensures 
              high availability and performance.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-6 mt-12">
              <div className="card-base">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#F0FDF4] flex items-center justify-center">
                  <Layers className="w-6 h-6 text-[#1F7A3A]" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Modular Design</h3>
                <p className="text-sm text-slate-600">Independent modules that can be configured and scaled separately</p>
              </div>
              <div className="card-base">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#F0FDF4] flex items-center justify-center">
                  <Settings className="w-6 h-6 text-[#1F7A3A]" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Configurable</h3>
                <p className="text-sm text-slate-600">Adapt to national regulations and specific export market requirements</p>
              </div>
              <div className="card-base">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#F0FDF4] flex items-center justify-center">
                  <History className="w-6 h-6 text-[#1F7A3A]" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Maintainable</h3>
                <p className="text-sm text-slate-600">Clean code practices ensure long-term sustainability and updates</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTAStrip 
        title="See Certisfy in action"
        subtitle="Schedule a personalized demo and discover how Certisfy can transform your certification process."
      />
    </div>
  );
}
